import React, { useEffect, useRef, useState } from "react";
import { Play, X } from "lucide-react";
import {
    Dialog,
    DialogTitle,
    DialogDescription,
    DialogPortal,
    DialogOverlay,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";

// Served from the FastAPI backend's StaticFiles mount so Cloudflare sees a
// correct video/mp4 MIME type and Accept-Ranges header (the frontend static
// bundle gets re-stamped as application/octet-stream by Cloudflare which
// breaks <video> playback). Same-origin, no third-party host.
// Bump VIDEO_VERSION whenever the source MP4 / poster is replaced. The query
// string forces browsers and CDNs (Cloudflare) to fetch the new file instead
// of serving a stale cached copy.
const VIDEO_VERSION = "3";
const VIDEO_SRC = `${process.env.REACT_APP_BACKEND_URL}/api/video-asset/intro-video.mp4?v=${VIDEO_VERSION}`;
const POSTER_SRC = `${process.env.REACT_APP_BACKEND_URL}/api/static/videos/intro-poster.jpg?v=${VIDEO_VERSION}`;

/**
 * Self-hosted intro video. The source MP4 is ultra-wide (2544×1080); we
 * letterbox it inside a 16:9 frame using `object-contain` so the full image
 * is always visible — never cropped, never stretched. The lightbox sits
 * inside the viewport with safe-area padding so the player is never cut off
 * at the screen edge.
 */
export default function IntroVideo() {
    const [open, setOpen] = useState(false);
    const videoRef = useRef(null);

    // Pause + rewind whenever the dialog closes so the next open starts fresh
    // and audio never keeps playing in the background.
    useEffect(() => {
        if (open || !videoRef.current) return;
        try {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        } catch (error) {
            // Some browsers throw if currentTime is set before metadata loads.
            // Non-fatal — the dialog will reset on next open regardless.
            console.warn("IntroVideo: reset on close failed", error);
        }
    }, [open]);

    return (
        <section
            id="intro-video"
            data-testid="intro-video-section"
            aria-labelledby="intro-video-heading"
            className="relative px-6 pb-16 pt-4 md:px-10 md:pb-24"
        >
            <div className="mx-auto w-full max-w-5xl">
                <div className="mb-8 max-w-2xl">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-brand-goldSoft">
                        Watch · 60-second intro
                    </p>
                    <h2
                        id="intro-video-heading"
                        className="mt-3 font-serif text-3xl text-brand-ivory sm:text-4xl"
                    >
                        See <span className="gold-text">how we work</span>.
                    </h2>
                    <p className="mt-3 text-base text-brand-muted">
                        Learn how Endurance Casualty helps protect what matters
                        most.
                    </p>
                </div>

                <Dialog open={open} onOpenChange={setOpen}>
                    {/* Thumbnail trigger — 16:9 frame, object-contain on poster */}
                    <button
                        type="button"
                        data-testid="intro-video-trigger"
                        aria-label="Watch intro video"
                        onClick={() => setOpen(true)}
                        className="group relative block w-full overflow-hidden rounded-[1.25rem] border border-[rgba(212,175,55,0.3)] bg-black shadow-[0_30px_70px_-30px_rgba(0,0,0,0.85)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                    >
                        <div className="relative aspect-video w-full">
                            <img
                                src={POSTER_SRC}
                                alt="Endurance Casualty intro video preview"
                                loading="lazy"
                                className="absolute inset-0 h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                            />
                            {/* Gradient overlay for text contrast */}
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-bg/70 via-transparent to-transparent" />
                            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.08),transparent_55%)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                            {/* Play button */}
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <span
                                    aria-hidden="true"
                                    className="relative flex h-20 w-20 items-center justify-center rounded-full border border-brand-gold/60 bg-[#0b0e13]/70 backdrop-blur-md transition-all duration-500 group-hover:h-24 group-hover:w-24 group-hover:border-brand-gold group-hover:bg-brand-gold/15 sm:h-24 sm:w-24"
                                >
                                    <span className="absolute inset-0 animate-ping rounded-full border border-brand-gold/30 opacity-60" />
                                    <Play
                                        className="ml-1 h-8 w-8 fill-brand-gold2 text-brand-gold2 transition-colors group-hover:fill-brand-gold group-hover:text-brand-gold sm:h-10 sm:w-10"
                                        strokeWidth={1}
                                    />
                                </span>
                            </div>

                            {/* Bottom badge */}
                            <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex items-center justify-between gap-4 sm:bottom-6 sm:left-6 sm:right-6">
                                <span className="rounded-full border border-brand-gold/60 bg-[#0b0e13]/85 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-brand-gold2 backdrop-blur sm:text-xs">
                                    Watch Intro Video
                                </span>
                                <span className="hidden text-xs text-brand-muted sm:inline">
                                    1 min · plays here on this page
                                </span>
                            </div>
                        </div>
                    </button>

                    {/* Lightbox — flex-centered with viewport padding to prevent edge clipping */}
                    <DialogPortal>
                        <DialogOverlay
                            className="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-black/85 p-4 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 sm:p-6 md:p-10"
                        >
                            <DialogPrimitive.Content
                                data-testid="intro-video-modal"
                                onOpenAutoFocus={(e) => e.preventDefault()}
                                className="relative z-10 mx-auto w-full max-w-5xl border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)] duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 sm:rounded-2xl"
                            >
                                <DialogTitle className="sr-only">
                                    Endurance Casualty intro video
                                </DialogTitle>
                                <DialogDescription className="sr-only">
                                    Learn how Endurance Casualty helps protect
                                    what matters most.
                                </DialogDescription>

                                {/* 16:9 frame with object-contain so the full
                                    video is visible. Black background fills any
                                    letterbox bars when the source aspect
                                    differs from 16:9. */}
                                <div className="relative w-full overflow-hidden rounded-t-2xl bg-black">
                                    <div className="relative aspect-video w-full">
                                        <video
                                            ref={videoRef}
                                            data-testid="intro-video-player"
                                            src={VIDEO_SRC}
                                            poster={POSTER_SRC}
                                            controls
                                            controlsList="nodownload"
                                            playsInline
                                            preload="metadata"
                                            className="absolute inset-0 h-full w-full object-contain"
                                        >
                                            Your browser does not support
                                            embedded video. You can still call
                                            us at 855.900.7405
                                            — a licensed agent will walk you
                                            through everything in person.
                                        </video>
                                    </div>

                                    <DialogPrimitive.Close
                                        aria-label="Close video"
                                        data-testid="intro-video-close"
                                        className="absolute right-3 top-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/50 bg-[#0b0e13]/85 text-brand-gold2 backdrop-blur transition-colors hover:bg-brand-gold hover:text-brand-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                                    >
                                        <X className="h-5 w-5" />
                                    </DialogPrimitive.Close>
                                </div>

                                <div className="flex flex-col items-start justify-between gap-3 border-t border-[rgba(212,175,55,0.2)] px-5 py-4 sm:flex-row sm:items-center sm:px-6">
                                    <p className="text-sm text-brand-muted">
                                        Learn how Endurance Casualty helps
                                        protect what matters most.
                                    </p>
                                    <a
                                        href="tel:+18559007405"
                                        className="text-xs uppercase tracking-[0.3em] text-brand-gold2 transition-colors hover:text-brand-gold"
                                    >
                                        Call 855.900.7405 →
                                    </a>
                                </div>
                            </DialogPrimitive.Content>
                        </DialogOverlay>
                    </DialogPortal>
                </Dialog>
            </div>
        </section>
    );
}
